import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { makeAutoObservable } from "mobx";

import { ApiConnector } from "../../api/api-connector";
import { harnessRoute, useHarnessChat } from "../../config/config";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  toolCalls?: string[];
}

// Two agents answer this chat: the one built into db-api, and harness.
// They speak the same event vocabulary, so switching is only a matter of
// where the turn is sent -- see handleEvent for the one place they differ.
//
// harness needs an explicit base: db-api's routes live under /api/v1, while
// harness serves /chat/stream at its own root.
const CHAT_TARGET = useHarnessChat
  ? { base: harnessRoute, path: "/chat/stream" }
  : { base: undefined, path: "/agent/chat/stream" };

function prettifyToolName(raw: string): string {
  const match = raw.match(/api_v\d+_(.+?)_(get|post|put|patch|delete)$/);
  if (match) {
    return match[1].replace(/_/g, "/");
  }
  return raw.replace(/_/g, " ");
}

export class ChatModel {
  messages: ChatMessage[] = [];
  input = "";
  loading = false;
  conversationId: string | null = null;
  activeTools: string[] = [];

  private abortController: AbortController | undefined;

  constructor(private api: ApiConnector) {
    makeAutoObservable(this);
  }

  async sendMessage() {
    const trimmed = this.input.trim();
    if (!trimmed || this.loading) return;

    this.messages.push({ role: "user", content: trimmed });
    this.messages.push({ role: "assistant", content: "", toolCalls: [] });
    this.input = "";
    this.loading = true;
    this.activeTools = [];

    this.abortController?.abort();
    const abort = new AbortController();
    this.abortController = abort;

    try {
      const response = await this.api.streamingFetch(
        CHAT_TARGET.path,
        { message: trimmed, conversation_id: this.conversationId },
        abort.signal,
        CHAT_TARGET.base,
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error("No response body");

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            this.handleEvent(event);
          } catch {
            // skip malformed JSON
          }
        }
      }
    } catch (err) {
      if (abort.signal.aborted) return;
      this.failLastMessage();
      console.error("Chat error:", err);
    } finally {
      this.loading = false;
      this.activeTools = [];
    }
  }

  private handleEvent(event: { type: string; [key: string]: unknown }) {
    const last = this.messages[this.messages.length - 1];
    if (!last || last.role !== "assistant") return;

    // Take the conversation id from whichever frame carries it rather than
    // from one named event: db-api puts it on `done`, harness on the
    // leading `meta`. Reading only `done` silently starts a new
    // conversation on every turn against harness.
    if (typeof event.conversation_id === "string") {
      this.conversationId = event.conversation_id;
    }

    switch (event.type) {
      case "text_delta":
        last.content += event.content as string;
        break;
      case "tool_call": {
        const toolName = prettifyToolName(event.tool_name as string);
        this.activeTools = [...this.activeTools, toolName];
        last.toolCalls = [...(last.toolCalls ?? []), toolName];
        break;
      }
      case "tool_result": {
        const toolName = prettifyToolName(event.tool_name as string);
        this.activeTools = this.activeTools.filter((t) => t !== toolName);
        break;
      }
      case "restart":
        // harness retried the step and will re-stream the reply from the
        // start; keeping what arrived would duplicate it.
        last.content = "";
        last.toolCalls = [];
        this.activeTools = [];
        break;
      case "error":
        this.failLastMessage();
        this.activeTools = [];
        break;
      case "done":
        this.activeTools = [];
        break;
    }
  }

  newConversation() {
    this.abortController?.abort();
    this.messages = [];
    this.conversationId = null;
    this.input = "";
    this.activeTools = [];
  }

  handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void this.sendMessage();
    }
  }

  dispose() {
    this.abortController?.abort();
  }

  // Appended rather than substituted: an error that arrives after text has
  // already streamed leaves a partial answer on screen, and replacing it
  // only when it is empty would let a truncated reply read as a complete one.
  private failLastMessage() {
    const last = this.messages[this.messages.length - 1];
    if (!last || last.role !== "assistant") return;
    const note = i18n._(msg`Sorry, something went wrong. Please try again.`);
    last.content = last.content ? `${last.content}\n\n${note}` : note;
  }
}

export function createChatModel(api: ApiConnector) {
  return new ChatModel(api);
}
